import { useState, useEffect } from 'react';
import { userService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui';
import {
  Users,
  UserPlus,
  UserMinus,
  Loader2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';

/**
 * Pagina assegnazione clienti-operatori (admin)
 */
function AssignmentsPage() {
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [operators, setOperators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filtro
  const [filterOperator, setFilterOperator] = useState('all');

  // Dialog assegnazione
  const [assignDialog, setAssignDialog] = useState({
    open: false,
    client: null,
    selectedOperator: '',
  });
  const [isAssigning, setIsAssigning] = useState(false);

  // Dialog rimozione assegnazione
  const [unassignDialog, setUnassignDialog] = useState({
    open: false,
    client: null,
  });
  const [isUnassigning, setIsUnassigning] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [clientsRes, operatorsRes] = await Promise.all([
        userService.getClients(),
        userService.getOperators(),
      ]);
      setClients(clientsRes.data?.clients || []);
      setOperators(operatorsRes.data?.operators || []);
    } catch (error) {
      toast.error('Errore', 'Impossibile caricare i dati');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!assignDialog.client || !assignDialog.selectedOperator) return;

    setIsAssigning(true);
    try {
      await userService.assignClient(assignDialog.client._id, assignDialog.selectedOperator);
      toast.success(
        'Cliente assegnato',
        `${assignDialog.client.firstName} ${assignDialog.client.lastName} è stato assegnato`
      );
      setAssignDialog({ open: false, client: null, selectedOperator: '' });
      loadData();
    } catch (error) {
      toast.error('Errore', error.response?.data?.message || 'Impossibile assegnare il cliente');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassign = async () => {
    if (!unassignDialog.client) return;

    setIsUnassigning(true);
    try {
      await userService.unassignClient(unassignDialog.client._id);
      toast.success(
        'Assegnazione rimossa',
        `${unassignDialog.client.firstName} ${unassignDialog.client.lastName} non è più assegnato`
      );
      setUnassignDialog({ open: false, client: null });
      loadData();
    } catch (error) {
      toast.error('Errore', error.response?.data?.message || 'Impossibile rimuovere l\'assegnazione');
    } finally {
      setIsUnassigning(false);
    }
  };

  // Filtra clienti
  const filteredClients = clients.filter((client) => {
    if (filterOperator === 'all') return true;
    if (filterOperator === 'unassigned') return !client.assignedOperator;
    return client.assignedOperator?._id === filterOperator;
  });

  // Statistiche
  const unassignedCount = clients.filter((c) => !c.assignedOperator).length;
  const assignedCount = clients.length - unassignedCount;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Assegnazione Clienti</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Gestisci le assegnazioni clienti-operatori
          </p>
        </div>
        <Button variant="outline" onClick={loadData} className="w-full sm:w-auto min-h-[44px] touch-manipulation">
          <RefreshCw className="mr-2 h-4 w-4" />
          Aggiorna
        </Button>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2.5 sm:p-3 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 flex-shrink-0">
                <Users size={20} className="sm:w-6 sm:h-6" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold">{clients.length}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Clienti totali</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2.5 sm:p-3 rounded-lg bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 flex-shrink-0">
                <UserPlus size={20} className="sm:w-6 sm:h-6" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold">{assignedCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Assegnati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`p-2.5 sm:p-3 rounded-lg flex-shrink-0 ${unassignedCount > 0 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                <AlertCircle size={20} className="sm:w-6 sm:h-6" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold">{unassignedCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Non assegnati</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtro */}
      <div className="flex gap-4">
        <Select value={filterOperator} onValueChange={setFilterOperator}>
          <SelectTrigger className="w-full sm:w-[280px]">
            <SelectValue placeholder="Filtra per operatore" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i clienti</SelectItem>
            <SelectItem value="unassigned">Non assegnati</SelectItem>
            {operators.map((op) => (
              <SelectItem key={op._id} value={op._id}>
                {op.firstName} {op.lastName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Lista clienti */}
      <Card>
        <CardHeader>
          <CardTitle>Clienti ({filteredClients.length})</CardTitle>
          <CardDescription>
            Clicca su un cliente per assegnarlo o modificare l'assegnazione
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredClients.length === 0 ? (
            <div className="text-center py-8">
              <Users className="mx-auto text-muted-foreground mb-4" size={48} />
              <p className="text-muted-foreground">Nessun cliente trovato</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClients.map((client) => (
                <div
                  key={client._id}
                  className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-medium">
                          {client.firstName?.[0]}
                          {client.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {client.firstName} {client.lastName}
                        </p>
                        <p className="text-sm text-muted-foreground">{client.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Info ospite */}
                  <div className="mb-3 text-sm">
                    <p>
                      <span className="text-muted-foreground">Ospite:</span>{' '}
                      {client.guestName || 'Non specificato'}
                    </p>
                    {client.guestRoom && (
                      <p>
                        <span className="text-muted-foreground">Stanza:</span> {client.guestRoom}
                      </p>
                    )}
                  </div>

                  {/* Stato assegnazione */}
                  <div className="mb-3">
                    {client.assignedOperator ? (
                      <Badge variant="success">
                        Assegnato a: {client.assignedOperator.firstName}{' '}
                        {client.assignedOperator.lastName}
                      </Badge>
                    ) : (
                      <Badge variant="warning">Non assegnato</Badge>
                    )}
                  </div>

                  {/* Azioni */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 min-h-[44px] touch-manipulation"
                      onClick={() =>
                        setAssignDialog({
                          open: true,
                          client,
                          selectedOperator: client.assignedOperator?._id || '',
                        })
                      }
                    >
                      <UserPlus className="mr-1 h-4 w-4" />
                      {client.assignedOperator ? 'Riassegna' : 'Assegna'}
                    </Button>
                    {client.assignedOperator && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-h-[44px] min-w-[44px] touch-manipulation"
                        onClick={() => setUnassignDialog({ open: true, client })}
                      >
                        <UserMinus className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog assegnazione */}
      <Dialog
        open={assignDialog.open}
        onOpenChange={(open) =>
          setAssignDialog({ open, client: null, selectedOperator: '' })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna Cliente</DialogTitle>
            <DialogDescription>
              Seleziona l'operatore a cui assegnare{' '}
              <strong>
                {assignDialog.client?.firstName} {assignDialog.client?.lastName}
              </strong>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Select
              value={assignDialog.selectedOperator}
              onValueChange={(value) =>
                setAssignDialog((prev) => ({ ...prev, selectedOperator: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleziona operatore" />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => (
                  <SelectItem key={op._id} value={op._id}>
                    {op.firstName} {op.lastName} ({op.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setAssignDialog({ open: false, client: null, selectedOperator: '' })
              }
            >
              Annulla
            </Button>
            <Button
              onClick={handleAssign}
              isLoading={isAssigning}
              disabled={!assignDialog.selectedOperator}
            >
              Assegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog rimozione assegnazione */}
      <Dialog
        open={unassignDialog.open}
        onOpenChange={(open) => setUnassignDialog({ open, client: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rimuovi Assegnazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler rimuovere l'assegnazione di{' '}
              <strong>
                {unassignDialog.client?.firstName} {unassignDialog.client?.lastName}
              </strong>
              ? Il cliente non sarà più gestito da nessun operatore.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnassignDialog({ open: false, client: null })}
            >
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleUnassign} isLoading={isUnassigning}>
              Rimuovi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AssignmentsPage;
